import React from 'react'
import {
  Drawer,
  List,
  Divider,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Box,
  TextField,
  InputAdornment,
  Typography,
} from '@mui/material'
import { Theme } from '@mui/material/styles'
import makeStyles from 'shared/makeStyles'
import createStyles from '@mui/styles/createStyles'
import SearchIcon from '@mui/icons-material/Search'
import ReceiptIcon from '@mui/icons-material/Receipt'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import PeopleIcon from '@mui/icons-material/People'
import ScatterPlotIcon from '@mui/icons-material/ScatterPlot'
import EmojiPeopleIcon from '@mui/icons-material/EmojiPeople'
import PagesIcon from '@mui/icons-material/Pages'
import LocalOfferIcon from '@mui/icons-material/LocalOffer'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import VpnKeyIcon from '@mui/icons-material/VpnKey'
import GroupIcon from '@mui/icons-material/Group'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import BusinessIcon from '@mui/icons-material/Business'
import PrintIcon from '@mui/icons-material/Print'
import ListIcon from '@mui/icons-material/List'
import EcoIcon from '@mui/icons-material/EnergySavingsLeaf'
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter'
import CheckIcon from '@mui/icons-material/Check'
import ImportExportIcon from '@mui/icons-material/ImportExport'
import GetAppIcon from '@mui/icons-material/GetApp'
// @ts-ignore
import Discount from '@mui/icons-material/Discount'
// @ts-ignore
import Park from '@mui/icons-material/Park'
// @ts-ignore
import InventoryIcon from '@mui/icons-material/Inventory'
import PaidIcon from '@mui/icons-material/Paid'
import HelpOutlineIcon from '@mui/icons-material/HelpCenterOutlined'
import { checkAccess, isUserSuperuser } from 'shared/auth'
import { IReduxState } from 'interfaces/IReduxState'

import { useSelector, useDispatch } from 'react-redux'
import { Link, useLocation } from 'react-router-dom'

import peeping from '../peeping.png'

const SearchQueryContext = React.createContext('')

interface ListItemLinkProps {
  icon?: React.ReactElement
  primary: string
  to: string
  isNested?: boolean
  closeDrawer: () => void
  shouldShow: boolean
  className?: string
  activeCue?: boolean
  exact?: boolean
}

interface ExpandableListItemProps {
  text: string
  isDrawerOpen: IsDrawerOpenInterface
  setIsDrawerOpen: (state: any) => void
  children?: React.ReactElement<ListItemLinkProps>[]
  icon?: React.ReactElement
}

interface IsDrawerOpenInterface {
  [key: string]: boolean
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    nested: {
      paddingLeft: theme.spacing(4),
    },
    chevron: {
      paddingLeft: theme.spacing(4),
    },
    icon: {
      minWidth: theme.spacing(4),
    },
    peeping: {
      position: 'absolute',
      right: 0,
      zIndex: -1,
      bottom: 0,
      opacity: 0.6,
    },
    login: {
      position: 'absolute',
      bottom: 0,
    },
    loginBox: {
      position: 'relative',
      height: '140px',
    },
    drawerPaper: {
      width: '220px',
    },
    list: {
      height: '100%',
      width: '220px',
      minWidth: '220px',
    },
    searchField: {
      margin: theme.spacing(2),
      width: 'calc(100% - 32px)',
    },
    userInfo: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      fontSize: '0.8rem',
      color: theme.palette.text.primary,
      position: 'absolute',
      bottom: '48px',
      width: '100%',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  })
)

export const ListItemLink = (props: ListItemLinkProps) => {
  const classes = useStyles()
  const {
    icon,
    primary,
    to,
    isNested,
    shouldShow,
    className = '',
    activeCue = true,
    exact = false,
  } = props
  const { pathname } = useLocation()

  const searchQuery = React.useContext(SearchQueryContext)
  const matchesSearch =
    !searchQuery || primary.toLowerCase().includes(searchQuery.toLowerCase())
  const normalizedPath = to.replace(/\/+$/, '') || '/'
  const isCurrent =
    activeCue &&
    (exact
      ? pathname === normalizedPath
      : pathname === normalizedPath || pathname.startsWith(`${normalizedPath}/`))

  return shouldShow && matchesSearch ? (
    <ListItemButton
      onClick={props.closeDrawer}
      component={Link}
      to={to}
      selected={isCurrent}
      aria-current={isCurrent ? 'page' : undefined}
      className={isNested ? className + classes.nested : className}
    >
      {icon ? (
        <ListItemIcon className={classes.icon}>{icon}</ListItemIcon>
      ) : null}
      <ListItemText primary={primary} />
    </ListItemButton>
  ) : null
}

export const ExpandableListItem = (props: ExpandableListItemProps) => {
  const classes = useStyles()
  const { isDrawerOpen, setIsDrawerOpen, text, icon } = props
  const toggleDrawer = () => {
    const newObject: { [key: string]: boolean } = { ...isDrawerOpen }
    Object.keys(isDrawerOpen).forEach((key) => {
      if (key === text) newObject[key] = !isDrawerOpen[key]
      else newObject[key] = false
    })
    setIsDrawerOpen(newObject)
  }

  const searchQuery = React.useContext(SearchQueryContext)
  const matchesSearch =
    !searchQuery || text.toLowerCase().includes(searchQuery.toLowerCase())

  const childrenMatchSearch =
    Array.isArray(props.children) &&
    props.children.some((child: React.ReactElement<ListItemLinkProps>) => {
      const childPrimary = child.props.primary || ''
      return (
        !searchQuery ||
        childPrimary.toLowerCase().includes(searchQuery.toLowerCase())
      )
    })

  const shouldShow =
    (matchesSearch || childrenMatchSearch) &&
    Array.isArray(props.children) &&
    props.children.some(
      (child: React.ReactElement<ListItemLinkProps>) => child.props.shouldShow
    )

  const isOpen = isDrawerOpen[text] || (!!searchQuery && childrenMatchSearch)

  return shouldShow ? (
    <div>
      <ListItemButton onClick={toggleDrawer}>
        {icon ? (
          <ListItemIcon className={classes.icon}>{icon}</ListItemIcon>
        ) : null}
        <ListItemText primary={text} />
        {props.children &&
          (isOpen ? (
            <ExpandMore className={classes.chevron} />
          ) : (
            <ExpandLess className={classes.chevron} />
          ))}
      </ListItemButton>
      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        {props.children}
      </Collapse>
    </div>
  ) : null
}

export default function SideDrawer() {
  const [isDrawerOpen, setIsDrawerOpen] = React.useState({
    Merek: false,
    Customer: false,
    User: false,
    Invoice: false,
    Label: false,
    'Order Kemasan': false,
    'Order Produk': false,
    Kemasan: false,
    Produksi: false,
    Ruahan: false,
    'Setengah Jadi': false,
    Jadi: false,
    'Sales Order Kosmetik': false,
    'Sales Order OT': false,
    'Surat Jalan': false,
    'Bahan Baku': false,
    'Formulir Kunjungan': false,
    Diskon: false,
    Export: false,
  })

  const [searchQuery, setSearchQuery] = React.useState('')

  const isSideDrawerOpen = useSelector(
    (state: IReduxState) => state.isSideDrawerOpen
  )
  const isSignedIn = useSelector(
    (state: IReduxState) => state.signInStatus === 'SIGNED_IN'
  )
  const groupAccessMapping = useSelector(
    (state: IReduxState) => state.groupAccessMapping
  )
  const userGroups = useSelector((state: IReduxState) => state.userGroups || [])
  const currentUser = useSelector((state: IReduxState) => state.currentUser)

  const [deferredPrompt, setDeferredPrompt] = React.useState<any>(null)

  React.useEffect(() => {
    const handleBeforeInstallPrompt = (e: any) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault()
      // Stash the event so it can be triggered later.
      setDeferredPrompt(e)
    }

    const handleAppInstalled = () => {
      setDeferredPrompt(null)
      console.log('PWA was installed')
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleAppInstalled)

    return () => {
      window.removeEventListener(
        'beforeinstallprompt',
        handleBeforeInstallPrompt
      )
      window.removeEventListener('appinstalled', handleAppInstalled)
    }
  }, [])

  const handleInstallClick = async () => {
    if (!deferredPrompt) return
    deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice
    console.log(`User response to the install prompt: ${outcome}`)
    setDeferredPrompt(null)
  }

  const dispatch = useDispatch()
  const classes = useStyles()
  const closeDrawer = () => dispatch({ type: 'CLOSE_SIDEDRAWER' })
  return (
    <Drawer
      classes={{ paper: classes.drawerPaper }}
      open={isSideDrawerOpen}
      onClose={closeDrawer}
    >
      <TextField
        className={classes.searchField}
        variant="outlined"
        placeholder="Cari..."
        size="small"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
      />
      <SearchQueryContext.Provider value={searchQuery}>
        <List className={classes.list}>
          <ExpandableListItem
            text="Customer"
            setIsDrawerOpen={setIsDrawerOpen}
            isDrawerOpen={isDrawerOpen}
            icon={<EmojiPeopleIcon />}
          >
            <ListItemLink
              to={`/customer/create`}
              primary="Customer"
              isNested
              icon={<AddIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'customers',
                'create'
              )}
            />
            <ListItemLink
              to={`/customer/manage`}
              primary="Pengaturan"
              isNested
              icon={<EditIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'customers',
                'update'
              )}
            />
            <ListItemLink
              to={`/customer/export`}
              primary="Export"
              isNested
              icon={<ImportExportIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'customers',
                'export'
              )}
            />
          </ExpandableListItem>
          <Divider />
          <ExpandableListItem
            text="Merek"
            setIsDrawerOpen={setIsDrawerOpen}
            isDrawerOpen={isDrawerOpen}
            icon={<LocalOfferIcon />}
          >
            <ListItemLink
              to={`/brand/create`}
              primary="Merek"
              isNested
              icon={<AddIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'brands',
                'create'
              )}
            />
            <ListItemLink
              to={`/brand/manage`}
              primary="Pengaturan"
              isNested
              icon={<EditIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'brands',
                'update'
              )}
            />
          </ExpandableListItem>
          <Divider />
          <ListItemLink
            to={`/purchaseOrder/list`}
            primary="Purchase Order"
            icon={<ListIcon />}
            closeDrawer={closeDrawer}
            shouldShow={checkAccess(
              userGroups,
              groupAccessMapping,
              'purchaseOrder',
              'list'
            )}
          />
          <Divider />
          <ExpandableListItem
            text="Bahan Baku"
            setIsDrawerOpen={setIsDrawerOpen}
            isDrawerOpen={isDrawerOpen}
            icon={<Park />}
          >
            <ListItemLink
              to={`/ingredient/create`}
              primary="Bahan Baku"
              isNested
              icon={<AddIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'ingredient',
                'create'
              )}
            />
            <ListItemLink
              to={`/ingredient/inventory`}
              primary="Gudang Bahan Baku"
              isNested
              icon={<InventoryIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'ingredient',
                'inventory'
              )}
            />
            <ListItemLink
              to={`/ingredient/request`}
              primary="Request Bahan Baku In/Out"
              isNested
              icon={<AddIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'ingredient',
                'request'
              )}
            />
            <ListItemLink
              to={`/ingredient/list`}
              primary="Daftar Request Bahan Baku In/Out"
              isNested
              icon={<ListIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'ingredient',
                'requestList'
              )}
            />
            <ListItemLink
              to={`/ingredient/manage`}
              primary="Pengaturan"
              isNested
              icon={<EditIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'ingredient',
                'update'
              )}
            />
            <ListItemLink
              to={`/ingredient/export/history`}
              primary="Export Riwayat"
              isNested
              icon={<ImportExportIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'ingredient',
                'export'
              )}
            />
          </ExpandableListItem>
          <Divider />
          <ExpandableListItem
            text="Kemasan"
            setIsDrawerOpen={setIsDrawerOpen}
            isDrawerOpen={isDrawerOpen}
            icon={<PagesIcon />}
          >
            <ListItemLink
              to={`/packaging/inventory/kosmetik`}
              primary="Gudang Kemasan (Kosmetik)"
              isNested
              icon={<InventoryIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'inventoryKosmetik',
                'inventoryPackaging'
              )}
            />
            <ListItemLink
              to={`/packaging/inventory/ot`}
              primary="Gudang Kemasan (OT)"
              isNested
              icon={<InventoryIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'inventoryOt',
                'inventoryPackaging'
              )}
            />

            <ListItemLink
              to={`/packaging/list`}
              primary="Daftar Request Kemasan In/Out"
              isNested
              icon={<ListIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'packaging',
                'requestList'
              )}
            />
            <ListItemLink
              to={`/packaging/request`}
              primary="Request Kemasan In/Out"
              isNested
              icon={<AddIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'packaging',
                'request'
              )}
            />
            <ListItemLink
              to={`/packaging/create`}
              primary="Kemasan"
              isNested
              icon={<AddIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'packaging',
                'create'
              )}
            />
            <ListItemLink
              to={`/packaging/manage`}
              primary="Pengaturan Set"
              isNested
              icon={<EditIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'packaging',
                'update'
              )}
            />
            <ListItemLink
              to={`/packaging/parts`}
              primary="Pengaturan Part"
              isNested
              icon={<EditIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'packaging',
                'update'
              )}
            />
            <ListItemLink
              to={`/packaging/export/history`}
              primary="Export Riwayat"
              isNested
              icon={<ImportExportIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'packaging',
                'export'
              )}
            />
          </ExpandableListItem>
          <Divider />
          <ExpandableListItem
            text="Ruahan"
            setIsDrawerOpen={setIsDrawerOpen}
            isDrawerOpen={isDrawerOpen}
            icon={<EcoIcon />}
          >
            <ListItemLink
              to={`/premix/inventory/kosmetik`}
              primary="Gudang Ruahan (Kosmetik)"
              isNested
              icon={<InventoryIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'inventoryKosmetik',
                'inventoryPremix'
              )}
            />
            <ListItemLink
              to={`/premix/inventory/ot`}
              primary="Gudang Ruahan (OT)"
              isNested
              icon={<InventoryIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'inventoryOt',
                'inventoryPremix'
              )}
            />

            <ListItemLink
              to={`/premix/request`}
              primary="Request Ruahan In/Out"
              isNested
              icon={<AddIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'premix',
                'request'
              )}
            />
            <ListItemLink
              to={`/premix/list`}
              primary="Daftar Request Ruahan In/Out"
              isNested
              icon={<ListIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'premix',
                'requestList'
              )}
            />
            <ListItemLink
              to={`/production/createProductionFormPremix`}
              primary="Permintaan Bikin Ruahan"
              isNested
              icon={<AddIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'production',
                'createProductionFormPremix'
              )}
            />
            <ListItemLink
              to={`/production/manageProductionFormPremix/kosmetik`}
              primary="Daftar PBOS (Kosmetik)"
              isNested
              icon={<ListIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'production',
                'productionFormListPremixKosmetik'
              )}
            />
            <ListItemLink
              to={`/production/manageProductionFormPremix/ot`}
              primary="Daftar PBOS (OT)"
              isNested
              icon={<ListIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'production',
                'productionFormListPremixOt'
              )}
            />
            <ListItemLink
              to={`/batch/inventory/kosmetik`}
              primary="Daftar Bets (Kosmetik)"
              isNested
              icon={<ListIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'batch',
                'inventoryKosmetik'
              )}
            />
            <ListItemLink
              to={`/batch/inventory/ot`}
              primary="Daftar Bets (OT)"
              isNested
              icon={<ListIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'batch',
                'inventoryOt'
              )}
            />
            <ListItemLink
              to={`/mixing/inventory/kosmetik`}
              primary="Daftar Mixing (Kosmetik)"
              isNested
              icon={<ListIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'inventoryKosmetik',
                'inventoryMixing'
              )}
            />
            <ListItemLink
              to={`/mixing/inventory/ot`}
              primary="Daftar Mixing (OT)"
              isNested
              icon={<ListIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'inventoryOt',
                'inventoryMixing'
              )}
            />

            <ListItemLink
              to={`/premix/manage`}
              primary="Pengaturan"
              isNested
              icon={<EditIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'premix',
                'update'
              )}
            />
            <ListItemLink
              to={`/premix/export/history`}
              primary="Export Riwayat"
              isNested
              icon={<ImportExportIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'premix',
                'export'
              )}
            />
            <ListItemLink
              to={`/batch/export`}
              primary="Export CSV (Batch)"
              isNested
              icon={<ImportExportIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'batch',
                'export'
              )}
            />
            <ListItemLink
              to={`/premix/create`}
              primary="Ruahan"
              isNested
              icon={<AddIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'premix',
                'create'
              )}
            />
            <ListItemLink
              to={`/changelog`}
              primary="Log Audit Ruahan"
              isNested
              icon={<ListIcon />}
              closeDrawer={closeDrawer}
              activeCue={false}
              shouldShow={isUserSuperuser(userGroups)}
            />
          </ExpandableListItem>
          <Divider />
          <ExpandableListItem
            text="Setengah Jadi"
            setIsDrawerOpen={setIsDrawerOpen}
            isDrawerOpen={isDrawerOpen}
            icon={<ScatterPlotIcon />}
          >
            <ListItemLink
              to={`/product/inventory/halfComplete/kosmetik`}
              primary="Gudang STJ (Kosmetik)"
              isNested
              icon={<InventoryIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'inventoryKosmetik',
                'inventorySetengahJadi'
              )}
            />
            <ListItemLink
              to={`/product/inventory/halfComplete/ot`}
              primary="Gudang STJ (OT)"
              isNested
              icon={<InventoryIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'inventoryOt',
                'inventorySetengahJadi'
              )}
            />

            <ListItemLink
              to={`/filling/inventory/kosmetik`}
              primary="Daftar Filling (Kosmetik)"
              isNested
              icon={<ListIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'inventoryKosmetik',
                'inventoryFilling'
              )}
            />
            <ListItemLink
              to={`/filling/inventory/ot`}
              primary="Daftar Filling (OT)"
              isNested
              icon={<ListIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'inventoryOt',
                'inventoryFilling'
              )}
            />

            <ListItemLink
              to={`/product/list`}
              primary="Daftar Request STJ In/Out"
              isNested
              icon={<ListIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'products',
                'requestList'
              )}
            />
            <ListItemLink
              to={`/product/request`}
              primary="Request STJ In/Out"
              isNested
              icon={<AddIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'products',
                'request'
              )}
            />
            <ListItemLink
              to={`/production/manageProductionFormHalfComplete`}
              primary="Daftar Permintaan Bikin STJ"
              isNested
              icon={<ListIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'production',
                'manageProductionFormHalfComplete'
              )}
            />
            <ListItemLink
              to={`/production/createProductionHalfComplete`}
              primary="Permintaan Bikin STJ"
              isNested
              icon={<AddIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'production',
                'createProductionHalfComplete'
              )}
            />
            <ListItemLink
              to={`/product/stj/export/history`}
              primary="Export Riwayat STJ"
              isNested
              icon={<ImportExportIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'products',
                'export'
              )}
            />
          </ExpandableListItem>
          <Divider />
          <ExpandableListItem
            text="Jadi"
            setIsDrawerOpen={setIsDrawerOpen}
            isDrawerOpen={isDrawerOpen}
            icon={<ScatterPlotIcon />}
          >
            <ListItemLink
              to={`/product/inventory/complete/kosmetik`}
              primary="Gudang Jadi (Kosmetik)"
              isNested
              icon={<InventoryIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'inventoryKosmetik',
                'inventoryJadi'
              )}
            />
            <ListItemLink
              to={`/product/inventory/complete/ot`}
              primary="Gudang Jadi (OT)"
              isNested
              icon={<InventoryIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'inventoryOt',
                'inventoryJadi'
              )}
            />

            <ListItemLink
              to={`/product/create`}
              primary="Jadi"
              isNested
              icon={<AddIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'products',
                'create'
              )}
            />
            <ListItemLink
              to={`/product/manage`}
              primary="Pengaturan"
              isNested
              icon={<EditIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'products',
                'update'
              )}
            />
            <ListItemLink
              to={`/product/costs`}
              primary="Harga Pokok (HPP)"
              isNested
              icon={<PaidIcon />}
              closeDrawer={closeDrawer}
              shouldShow={isUserSuperuser(userGroups)}
            />
            <ListItemLink
              to={`/label/inventory/kosmetik`}
              primary="Daftar Labeling (Kosmetik)"
              isNested
              icon={<ListIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'inventoryKosmetik',
                'inventoryLabel'
              )}
            />
            <ListItemLink
              to={`/label/inventory/ot`}
              primary="Daftar Labeling (OT)"
              isNested
              icon={<ListIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'inventoryOt',
                'inventoryLabel'
              )}
            />

            <ListItemLink
              to={`/barangJadi/list`}
              primary="Daftar Request Barang Jadi In/Out"
              isNested
              icon={<ListIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'barangJadi',
                'requestList'
              )}
            />
            <ListItemLink
              to={`/barangJadi/request`}
              primary="Request Barang Jadi In/Out"
              isNested
              icon={<AddIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'barangJadi',
                'request'
              )}
            />
            <ListItemLink
              to={`/product/jadi/export/history`}
              primary="Export Riwayat Barang Jadi"
              isNested
              icon={<ImportExportIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'products',
                'export'
              )}
            />
          </ExpandableListItem>
          <Divider />
          <ExpandableListItem
            text="Order Produk"
            setIsDrawerOpen={setIsDrawerOpen}
            isDrawerOpen={isDrawerOpen}
            icon={<MenuBookIcon />}
          >
            <ListItemLink
              to={`/order/create`}
              primary="Order"
              isNested
              icon={<AddIcon />}
              closeDrawer={closeDrawer}
              activeCue={false}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'orders',
                'create'
              )}
            />
            <ListItemLink
              to={`/order/queue`}
              primary="Daftar Admin Order Produk"
              isNested
              icon={<ListIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'orders',
                'queue'
              )}
            />
            <ListItemLink
              to={`/order/list`}
              primary="Daftar Marketing Order"
              isNested
              icon={<ListIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'orders',
                'list'
              )}
            />
            <ListItemLink
              to={`/order/manage`}
              primary="Pengaturan Order Produk"
              isNested
              icon={<EditIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'orders',
                'update'
              )}
            />
          </ExpandableListItem>
          <Divider />
          <ExpandableListItem
            text="Order Kemasan"
            setIsDrawerOpen={setIsDrawerOpen}
            isDrawerOpen={isDrawerOpen}
            icon={<MenuBookIcon />}
          >
            <ListItemLink
              to={`/order/create`}
              primary="Order"
              isNested
              icon={<AddIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'orders',
                'create'
              )}
            />
            <ListItemLink
              to={`/order/packaging/queue`}
              primary="Daftar Order Admin (Kemasan)"
              isNested
              icon={<ListIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'orders',
                'packagingQueue'
              )}
            />
          </ExpandableListItem>
          <Divider />
          <ExpandableListItem
            text="Sales Order Kosmetik"
            setIsDrawerOpen={setIsDrawerOpen}
            isDrawerOpen={isDrawerOpen}
            icon={<BusinessIcon />}
          >
            <ListItemLink
              to={`/salesOrder/create`}
              primary="Sales Order"
              isNested
              icon={<AddIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'salesOrders',
                'create'
              )}
            />
            <ListItemLink
              to={`/salesOrder/factoryQueueLws`}
              primary="Daftar Produksi Kosmetik"
              isNested
              icon={<ListIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'salesOrders',
                'factoryQueueLws'
              )}
            />
            <ListItemLink
              to={`/salesOrder/readyQueueLws`}
              primary="Daftar Jadi Kosmetik"
              isNested
              icon={<CheckIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'salesOrders',
                'readyQueueLws'
              )}
            />
            <ListItemLink
              to={`/salesOrder/manage/lws`}
              primary="Pengaturan Kosmetik"
              isNested
              icon={<EditIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'salesOrders',
                'updateLws'
              )}
            />
          </ExpandableListItem>
          <Divider />
          <ExpandableListItem
            text="Sales Order OT"
            setIsDrawerOpen={setIsDrawerOpen}
            isDrawerOpen={isDrawerOpen}
            icon={<BusinessIcon />}
          >
            <ListItemLink
              to={`/salesOrder/create`}
              primary="Sales Order OT"
              isNested
              icon={<AddIcon />}
              closeDrawer={closeDrawer}
              activeCue={false}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'salesOrders',
                'create'
              )}
            />
            <ListItemLink
              to={`/salesOrder/factoryQueueOt`}
              primary="Daftar Produksi OT"
              isNested
              icon={<ListIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'salesOrders',
                'factoryQueueOt'
              )}
            />
            <ListItemLink
              to={`/salesOrder/readyQueueOt`}
              primary="Daftar Jadi OT"
              isNested
              icon={<CheckIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'salesOrders',
                'readyQueueOt'
              )}
            />
            <ListItemLink
              to={`/salesOrder/manage/ot`}
              primary="Pengaturan OT"
              isNested
              icon={<EditIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'salesOrders',
                'updateOt'
              )}
            />
          </ExpandableListItem>
          <Divider />
          <ExpandableListItem
            text="Invoice"
            setIsDrawerOpen={setIsDrawerOpen}
            isDrawerOpen={isDrawerOpen}
            icon={<ReceiptIcon />}
          >
            <ListItemLink
              to={`/invoice/queue`}
              primary="Daftar"
              isNested
              icon={<PrintIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'invoices',
                'queue'
              )}
            />
            <ListItemLink
              to={`/invoice/list`}
              primary="Daftar Marketing"
              isNested
              icon={<ListIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'invoices',
                'list'
              )}
            />
            <ListItemLink
              to={`/invoice/manage`}
              primary="Pengaturan"
              isNested
              icon={<EditIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'invoices',
                'update'
              )}
            />
            <ListItemLink
              to={`/invoice/cogs`}
              primary="COGS Invoice"
              isNested
              icon={<PaidIcon />}
              closeDrawer={closeDrawer}
              exact
              shouldShow={isUserSuperuser(userGroups)}
            />
            <ListItemLink
              to={`/invoice/cogs/batch`}
              primary="COGS Bulanan"
              isNested
              icon={<PaidIcon />}
              closeDrawer={closeDrawer}
              shouldShow={isUserSuperuser(userGroups)}
            />
          </ExpandableListItem>
          <Divider />
          <ListItemLink
            to={`/deliveryOrder/queue`}
            primary="Daftar Surat Jalan"
            icon={<PrintIcon />}
            closeDrawer={closeDrawer}
            shouldShow={checkAccess(
              userGroups,
              groupAccessMapping,
              'deliveryOrder',
              'queue'
            )}
          />
          <Divider />
          <ExpandableListItem
            text="Export"
            setIsDrawerOpen={setIsDrawerOpen}
            isDrawerOpen={isDrawerOpen}
            icon={<ImportExportIcon />}
          >
            <ListItemLink
              to={`/gudang/export`}
              primary="Riwayat Gudang"
              isNested
              icon={<ImportExportIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'batch',
                'export'
              )}
            />
            <ListItemLink
              to={`/ingredient/export`}
              primary="Bahan Baku"
              isNested
              icon={<ImportExportIcon />}
              closeDrawer={closeDrawer}
              exact
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'ingredient',
                'export'
              )}
            />
            <ListItemLink
              to={`/packaging/export`}
              primary="Kemasan"
              isNested
              icon={<ImportExportIcon />}
              closeDrawer={closeDrawer}
              exact
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'packaging',
                'export'
              )}
            />
            <ListItemLink
              to={`/premix/export`}
              primary="Ruahan (Premix)"
              isNested
              icon={<ImportExportIcon />}
              closeDrawer={closeDrawer}
              exact
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'premix',
                'export'
              )}
            />
            <ListItemLink
              to={`/batch/export`}
              primary="Batch"
              isNested
              icon={<ImportExportIcon />}
              closeDrawer={closeDrawer}
              activeCue={false}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'batch',
                'export'
              )}
            />
            <ListItemLink
              to={`/customer/export`}
              primary="Customer"
              isNested
              icon={<ImportExportIcon />}
              closeDrawer={closeDrawer}
              activeCue={false}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'customers',
                'export'
              )}
            />
            <ListItemLink
              to={`/product/export`}
              primary="Produk Jadi"
              isNested
              icon={<ImportExportIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'products',
                'export'
              )}
            />
            <ListItemLink
              to={`/order/export`}
              primary="Order Produk"
              isNested
              icon={<ImportExportIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'orders',
                'export'
              )}
            />
            <ListItemLink
              to={`/invoice/export`}
              primary="Invoice"
              isNested
              icon={<ImportExportIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'invoices',
                'export'
              )}
            />
            <ListItemLink
              to={`/salesOrder/export`}
              primary="Sales Order"
              isNested
              icon={<ImportExportIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'salesOrders',
                'export'
              )}
            />
          </ExpandableListItem>
          <Divider />
          <ListItemLink
            to={`/employee/salesReport`}
            primary="Sales Report"
            icon={<BusinessCenterIcon />}
            closeDrawer={closeDrawer}
            shouldShow={checkAccess(
              userGroups,
              groupAccessMapping,
              'employees',
              'salesReport'
            )}
          />
          <Divider />
          <ExpandableListItem
            text="User"
            setIsDrawerOpen={setIsDrawerOpen}
            isDrawerOpen={isDrawerOpen}
            icon={<PeopleIcon />}
          >
            <ListItemLink
              to={`/employee/create`}
              primary="User"
              isNested
              icon={<AddIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'employees',
                'create'
              )}
            />
            <ListItemLink
              to={`/employee/manage`}
              primary="Pengaturan"
              isNested
              icon={<EditIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'employees',
                'update'
              )}
            />
            <ListItemLink
              to={`/employee/groups`}
              primary="Grup"
              isNested
              icon={<GroupIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'employees',
                'createGroups'
              )}
            />
          </ExpandableListItem>
          <Divider />
          <ExpandableListItem
            text="Formulir Kunjungan"
            setIsDrawerOpen={setIsDrawerOpen}
            isDrawerOpen={isDrawerOpen}
            icon={<PeopleIcon />}
          >
            <ListItemLink
              to={`/employee/manageCheckin`}
              primary="Pengaturan"
              isNested
              icon={<EditIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'employees',
                'createCheckin'
              )}
            />
            <ListItemLink
              to={`/employee/createCheckin`}
              primary="Formulir Kunjungan"
              isNested
              icon={<AddIcon />}
              closeDrawer={closeDrawer}
              shouldShow={checkAccess(
                userGroups,
                groupAccessMapping,
                'employees',
                'createCheckin'
              )}
            />
          </ExpandableListItem>
          <ListItemLink
            to={`/product/discount`}
            primary="Paket Diskon"
            icon={<Discount />}
            closeDrawer={closeDrawer}
            shouldShow={checkAccess(
              userGroups,
              groupAccessMapping,
              'products',
              'discounts'
            )}
          />
          {isUserSuperuser(userGroups) && (
            <>
              <Divider />
              <ListItemLink
                to={`/changelog`}
                primary="Changelog"
                icon={<ListIcon />}
                closeDrawer={closeDrawer}
                shouldShow={true}
              />
              <ListItemLink
                to={`/statistics`}
                primary="Statistik"
                icon={<ScatterPlotIcon />}
                closeDrawer={closeDrawer}
                shouldShow={checkAccess(
                  userGroups,
                  groupAccessMapping,
                  'statistics',
                  'view'
                )}
              />
            </>
          )}
          <Divider />
          <ListItemLink
            to="/help"
            primary="Pusat Bantuan / Help Center"
            icon={<HelpOutlineIcon />}
            closeDrawer={closeDrawer}
            shouldShow={isSignedIn}
          />
          <Divider />
          {deferredPrompt && (
            <>
              <Divider />
              <ListItemButton onClick={handleInstallClick}>
                <ListItemIcon className={classes.icon}>
                  <GetAppIcon />
                </ListItemIcon>
                <ListItemText primary="Install App" />
              </ListItemButton>
            </>
          )}
          <Divider />
          <Box className={classes.loginBox}>
            {isSignedIn && currentUser && (
              <Typography className={classes.userInfo}>
                Logged in as: {currentUser.attributes?.name || currentUser.username}
              </Typography>
            )}
            <ListItemLink
              to={isSignedIn ? `/logout` : '/login'}
              primary={isSignedIn ? 'Logout' : 'Login'}
              icon={<VpnKeyIcon />}
              closeDrawer={closeDrawer}
              shouldShow
              className={classes.login}
            />
            <img
              alt="peeping"
              className={classes.peeping}
              src={String(peeping)}
            />
          </Box>
        </List>
      </SearchQueryContext.Provider>
    </Drawer>
  )
}
